import {
  Anchor,
  Button,
  CopyButton,
  Group,
  Loader,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { staffHorizon } from '@web/common';
import type {
  QuestionnaireCheckTargetDto,
  UserQuestionnaireStatusDto,
} from '@web/common';
import { TitledTable } from '../Nachversand/RetrievalTableParts';
import { fmtDate } from '../Nachversand/BdmRetrievalsSection.helpers';
import type { QuestionnaireCheckState } from '../../../store/QuestionnaireStatusStore';

const kindLabel = (q: UserQuestionnaireStatusDto, t: TFunction): string => {
  switch (q.kind) {
    case 'REOCCURING':
      return t('Kind.REOCCURING', {
        horizon: staffHorizon(q.horizon ?? 0),
        wave: q.wave ?? 0,
      });
    case 'BAG':
      return q.horizon === 1 ? t('Kind.BAG_H1') : t('Kind.BAG');
    default:
      return t(`Kind.${q.kind}`);
  }
};

const StateCell = ({ q }: { q: UserQuestionnaireStatusDto }) => {
  const { t } = useTranslation('questionnaire-status');

  if (q.state === 'FILLED_OUT') {
    return (
      <Text size="14px">
        {q.filledOutAt
          ? t('State.FILLED_OUT_ON', { date: fmtDate(q.filledOutAt) })
          : t('State.FILLED_OUT')}
      </Text>
    );
  }
  return (
    <Text size="14px" c="dimmed" fs="italic">
      {t(`State.${q.state}`)}
    </Text>
  );
};

const CheckCell = ({
  q,
  checkState,
  isCheckingAll,
  onCheck,
}: {
  q: UserQuestionnaireStatusDto;
  checkState: QuestionnaireCheckState | undefined;
  isCheckingAll: boolean;
  onCheck: (target: QuestionnaireCheckTargetDto) => void;
}) => {
  const { t } = useTranslation('questionnaire-status');
  const { checkTarget } = q;
  const isChecking = checkState?.type === 'CHECKING';

  return (
    <Stack gap="4px" align="flex-start">
      {q.checkable && checkTarget && (
        <Button
          variant="light"
          size="compact-xs"
          loading={isChecking}
          disabled={isCheckingAll && !isChecking}
          onClick={() => onCheck(checkTarget)}
        >
          {t('Check.Button')}
        </Button>
      )}
      {checkState?.type === 'DONE' && (
        <Text size="12px" c="dimmed">
          {t(`Check.Outcome.${checkState.outcome}`)}
        </Text>
      )}
      {checkState?.type === 'FAILED' && (
        <Text size="12px" c="red">
          {t('Check.Failed')}
        </Text>
      )}
    </Stack>
  );
};

type Props = {
  questionnaires: UserQuestionnaireStatusDto[];
  isLoading: boolean;
  loadFailed: boolean;
  onRetry: () => void;
  checkState: (
    target: QuestionnaireCheckTargetDto,
  ) => QuestionnaireCheckState | undefined;
  isCheckingAll: boolean;
  // also a single row check: "check all" waits for it
  isAnyCheckRunning: boolean;
  onCheck: (target: QuestionnaireCheckTargetDto) => void;
  onCheckAll: () => void;
};

const QuestionnaireStatusTableView = ({
  questionnaires,
  isLoading,
  loadFailed,
  onRetry,
  checkState,
  isCheckingAll,
  isAnyCheckRunning,
  onCheck,
  onCheckAll,
}: Props) => {
  const { t } = useTranslation('questionnaire-status');

  if (loadFailed) {
    return (
      <Stack gap="12px" align="flex-start">
        <Text c="red" size="14px">
          {t('LoadError')}
        </Text>
        <Button variant="light" size="xs" onClick={onRetry}>
          {t('Retry')}
        </Button>
      </Stack>
    );
  }

  if (isLoading) {
    return <Loader size="sm" />;
  }

  if (questionnaires.length === 0) {
    return (
      <Text c="dimmed" size="14px">
        {t('Empty')}
      </Text>
    );
  }

  return (
    <TitledTable
      title={t('Title')}
      columns={[
        t('Columns.Questionnaire'),
        t('Columns.DueFrom'),
        t('Columns.Status'),
        t('Columns.Check'),
        t('Columns.Link'),
      ]}
      action={
        questionnaires.some((q) => q.checkable) && (
          <Button
            variant="light"
            size="xs"
            loading={isCheckingAll}
            disabled={isAnyCheckRunning && !isCheckingAll}
            onClick={onCheckAll}
          >
            {t('Check.All')}
          </Button>
        )
      }
    >
      {questionnaires.map((q, index) => (
        // the import does not prevent two links of the same kind and round
        <Table.Tr key={`${q.kind}-${q.horizon}-${q.wave}-${q.link ?? index}`}>
          <Table.Td fw="500">{kindLabel(q, t)}</Table.Td>
          <Table.Td>
            <Text size="14px">{q.dueFrom ? fmtDate(q.dueFrom) : '–'}</Text>
          </Table.Td>
          <Table.Td>
            <StateCell q={q} />
          </Table.Td>
          <Table.Td>
            <CheckCell
              q={q}
              checkState={q.checkTarget ? checkState(q.checkTarget) : undefined}
              isCheckingAll={isCheckingAll}
              onCheck={onCheck}
            />
          </Table.Td>
          <Table.Td>
            {q.link ? (
              <Group gap="12px" wrap="nowrap">
                <Anchor
                  href={q.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="14px"
                >
                  {t('OpenLink')}
                </Anchor>
                <CopyButton value={q.link}>
                  {({ copied, copy }) => (
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color={copied ? 'primary' : 'gray'}
                      onClick={copy}
                    >
                      {copied ? t('Copied') : t('CopyLink')}
                    </Button>
                  )}
                </CopyButton>
              </Group>
            ) : (
              <Text size="12px" c="dimmed">
                –
              </Text>
            )}
          </Table.Td>
        </Table.Tr>
      ))}
    </TitledTable>
  );
};

// observer: checkState reads the check results from the store
export const QuestionnaireStatusTable = observer(QuestionnaireStatusTableView);
